import type { MutationMeta, QueryMeta } from '@tanstack/react-query';

export type ErrorHandler = string | (() => void);

export type ApiMetaProps = {
  [status: string]: {
    [code: string]: ErrorHandler;
  };
};

export const DEFAULT_API_ERROR_MESSAGES = {
  401: { '*': 'Your session has expired. Please sign in.' },
  403: { '*': "You don't have permission to perform this action." },
  404: {
    '*': 'Resource not found. Please refresh the page or contact the administrator.',
  },
  422: { '*': 'Invalid request.' },
  500: { '*': 'Something went wrong. Try again later.' },
} as const satisfies ApiMetaProps;

/**
 * Relaxed override shape: schema-typed statuses provide autocomplete for known
 * literal codes, but every code is optional and additional codes (including the
 * '*' wildcard) are accepted. Extra statuses the schema doesn't document are
 * also allowed via the ApiMetaProps index signature.
 */
type ApiMetaOverrides<T extends ApiMetaProps> = ApiMetaProps & {
  [Status in keyof T]?: Partial<T[Status]> & Record<string, ErrorHandler>;
};

export const createApiMeta = <T extends ApiMetaProps>(
  overrides: ApiMetaOverrides<T>
): ApiMetaProps => {
  const merged: ApiMetaProps = {};

  for (const [status, codes] of Object.entries(DEFAULT_API_ERROR_MESSAGES)) {
    merged[status] = { ...codes };
  }

  for (const [status, codes] of Object.entries(overrides)) {
    merged[status] = { ...(merged[status] ?? {}), ...codes };
  }

  return merged;
};

const isErrorHandler = (value: unknown): value is ErrorHandler =>
  typeof value === 'string' || typeof value === 'function';

const isApiMetaProps = (value: unknown): value is ApiMetaProps => {
  if (!value || typeof value !== 'object') return false;
  for (const [statusKey, codes] of Object.entries(
    value as Record<string, unknown>
  )) {
    if (!/^\d+$/.test(statusKey)) return false;
    if (!codes || typeof codes !== 'object') return false;
    for (const handler of Object.values(codes as Record<string, unknown>)) {
      if (!isErrorHandler(handler)) return false;
    }
  }
  return true;
};

export const validateApiMeta = (
  meta: QueryMeta | MutationMeta | undefined
): ApiMetaProps | null => {
  if (!isApiMetaProps(meta)) return null;
  return meta;
};
